"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/atoms/Accordion";
import { CarValuationSeoFaq } from "@interfaces/carValuationSeo.types";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import styles from "./CarValuationFaqSection.module.scss";

interface CarValuationFaqSectionProps {
  title: string;
  faqs: CarValuationSeoFaq[];
}

const DEFAULT_VISIBLE_FAQ_COUNT = 4;

export default function CarValuationFaqSection({
  title,
  faqs,
}: CarValuationFaqSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const visibleFaqs = useMemo(
    () =>
      isHydrated && !showAll ? faqs.slice(0, DEFAULT_VISIBLE_FAQ_COUNT) : faqs,
    [faqs, isHydrated, showAll]
  );

  const shouldShowToggle = faqs.length > DEFAULT_VISIBLE_FAQ_COUNT;

  return (
    <div className={styles.faqLayout}>
      <div className={styles.titleBlock}>
        <h2 className={styles.title}>{title}</h2>
      </div>

      <div className={styles.contentBlock}>
        {isHydrated ? (
          <Accordion
            type="single"
            defaultValue={visibleFaqs[0] ? `${visibleFaqs[0].question}-0` : ""}
            className={styles.accordion}
          >
            {visibleFaqs.map((faq, index) => {
              const itemValue = `${faq.question}-${index}`;

              return (
              <AccordionItem
                key={itemValue}
                value={itemValue}
                className={`${styles.faqItem} ${
                  index === 0 ? styles.firstFaqItem : ""
                }`}
              >
                <AccordionTrigger
                  value={itemValue}
                  className={styles.faqTrigger}
                  theme="dark"
                  iconWidth={20}
                  iconHeight={20}
                >
                  <span className={styles.faqQuestion}>{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent
                  value={itemValue}
                  className={styles.faqContent}
                >
                  <p className={styles.faqAnswer}>{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
              );
            })}
          </Accordion>
        ) : (
          <div className={styles.accordion}>
            {visibleFaqs.map((faq, index) => (
              <details
                key={`${faq.question}-${index}`}
                className={`${styles.faqItem} ${
                  index === 0 ? styles.firstFaqItem : ""
                }`}
                open={index === 0}
              >
                <summary className={styles.faqSummary}>
                  <span className={styles.faqQuestion}>{faq.question}</span>
                  <Image
                    src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
                    alt=""
                    width={20}
                    height={20}
                    className={styles.faqSummaryIcon}
                    aria-hidden="true"
                  />
                </summary>
                <p className={styles.faqAnswer}>{faq.answer}</p>
              </details>
            ))}
          </div>
        )}

        {isHydrated && shouldShowToggle ? (
          <button
            type="button"
            className={styles.showMoreButton}
            onClick={() => setShowAll((value) => !value)}
          >
            <span>{showAll ? "Show less" : "Show more"}</span>
            <Image
              src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
              alt=""
              width={24}
              height={24}
              className={`${styles.showMoreChevron} ${
                showAll ? styles.showMoreChevronUp : ""
              }`}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>
    </div>
  );
}
